// What to open, and how: a dropped File, a `?trace=` URL to a trace, a run directory
// (`.../runs/<key>/`) whose trace and meta.json are found by name, or `{ run: "<key>" }`
// -- a run named by its key alone, to be found. The bytes are read in the worker; this
// module only decides the source, fetches the sibling meta.json for gen/shape, and
// reports progress.
//
// For a `?trace=` or a `?run=` the page's bootstrap (index.html) has already done all
// of that before this module was even fetched: it holds the worker, the run's
// meta.json and the response, the worker is reading, and `early.base` says which of
// the two places a key was found in. Then there is nothing left here but to take the
// messages over. The path below is what happens when the bootstrap guessed the file
// wrong, or when a File is dropped.

import { mark, marks } from "./perf.js";
import { API, apiIsLocal } from "./api.js";

// The files a run directory may hold, best first.
const CANDIDATES = ["trace.bin.gz", "trace.bin", "trace.jsonl.gz", "trace.jsonl"];

export async function loadFrom(source, opts = {}, onProgress = () => {}) {
  mark("open");
  const early = takeEarly(source);
  const key = typeof source === "string" || !source ? null : source.run || null;
  let src = source, name, meta = null, res = null, started = false;
  if (typeof source === "string" || key) {
    const metaName = "meta.json";
    // A key has to be looked for; the bootstrap has usually settled that already and
    // says where it landed. A `?trace=` URL says where it is itself.
    const found = key && !early ? await findRun(key, metaName) : null;
    let url = new URL(key ? (early ? early.base : found.base) : source, location.href);
    started = early ? await early.started : false;
    meta = early ? await early.meta : found ? found.meta : await fetchJson(new URL(metaName, url));
    mark("meta");
    if (started) url = new URL(early.pick);
    else if (found && found.res) { res = found.res; url = new URL(res.url); mark("probe"); }
    else if (url.pathname.endsWith("/")) {
      res = await probe(url, early && early.pick);
      if (!res) throw new Error(`no trace under ${url}`);
      url = new URL(res.url);
      mark("probe");
    }
    src = url.href;
    name = decodeURIComponent(url.pathname.split("/").filter(Boolean).slice(-2).join("/"));
  } else {
    name = source.name;
  }
  const topo = meta && meta.topology;
  const gen = opts.gen || (topo && topo.gen) || null;
  const shape = opts.shape || (topo && topo.shape) || null;
  const msg = { src, gen, topology: shape, pageOrigin: performance.timeOrigin, wasm: false };
  if (res) { msg.stream = res.body; msg.size = res.headers.get("content-encoding") ? 0 : +res.headers.get("content-length") || 0; }
  // No event count is known before the trace is read, so progress is by bytes.
  const events = null;
  const result = await runWorker(msg, (p) => onProgress(events ? { ...p, events } : p), early, started);
  return { ...result, name, meta, source: typeof source === "string" || key ? src : null };
}

// Where the run with this key is. Beside the page (`runs/<key>/`) is where a server
// and an export both keep the runs they have; a run the page has not got -- one too
// big for the host to publish, or one a visitor's own submission made -- is at the
// API, when that is another origin (js/api.js). The pack and the meta.json the
// decision asks for are the ones the load needs anyway, so looking costs no request
// the load would not have spent: the run is here if either answers, and at the API
// otherwise.
async function findRun(key, metaName) {
  const here = new URL(`runs/${key}/`, location.href);
  const away = apiIsLocal ? null : new URL(`runs/${key}/`, API);
  let [meta, res] = await look(here, metaName);
  if (!away || meta || res) return { base: here.href, meta, res };
  [meta, res] = await look(away, metaName);
  return { base: away.href, meta, res };
}

function look(base, metaName) {
  return Promise.all([fetchJson(new URL(metaName, base)), fetchOk(new URL("trace.bin.gz", base))]);
}

async function fetchOk(url) {
  try {
    const r = await fetch(url);
    if (r.ok) return r;
    if (r.body) r.body.cancel();
  } catch (_) { /* not there */ }
  return null;
}

// The bootstrap's work, if it is about the run we are opening; anything else it started
// is dropped, worker and all.
function takeEarly(source) {
  const e = window.__early;
  if (!e) return null;
  delete window.__early;
  const mine = typeof source === "string" ? e.trace === source
    : !!(source && source.run && e.run === source.run);
  if (mine) return e;
  e.worker.terminate();
  return null;
}

function runWorker(msg, onProgress, early, started) {
  return new Promise((resolve, reject) => {
    const w = early ? early.worker : new Worker(new URL("./pack.worker.js", import.meta.url), { type: "module" });
    const pending = new Map();
    let reqId = 0, listing = null;
    const take = (data) => {
      if (data.type === "progress") onProgress(data);
      else if (data.type === "render") { const p = pending.get(data.reqId); pending.delete(data.reqId); if (p) (data.error ? p.reject(new Error(data.error)) : p.resolve(data)); }
      else if (data.type === "listing") { const p = listing; listing = null; w.terminate(); if (p) p.resolve(data.text); }
      else if (data.type === "done") {
        if (data.keepAlive || data.hasText) {
          // The worker holds the bundles' text -- an open trace, or a pack's own
          // columns: rendering a range of them goes through it, and a listing asks
          // for the whole table at once (disasm.js), which ends the worker.
          data.render = (key, first, count) => new Promise((res, rej) => { const id = ++reqId; pending.set(id, { resolve: res, reject: rej }); w.postMessage({ type: "render", reqId: id, key, first, count }); });
          data.close = () => w.terminate();
          if (data.hasText) data.listing = () => new Promise((res, rej) => { listing = { resolve: res, reject: rej }; w.postMessage({ type: "listing" }); });
        } else w.terminate();
        if (data.marks) marks.push(...data.marks);
        mark("transfer");
        resolve(data);
      }
      else if (data.type === "error") { w.terminate(); reject(new Error(data.message)); }
    };
    w.onmessage = ({ data }) => take(data);
    w.onerror = (e) => { w.terminate(); reject(new Error(e.message || "worker failed")); };
    if (early) for (const data of early.queue.splice(0)) take(data);
    if (!started) w.postMessage(msg, msg.stream ? [msg.stream] : []);
  });
}

async function fetchJson(url) {
  try {
    const r = await fetch(url);
    if (!r.ok) return null;
    return await r.json();
  } catch (_) {
    return null;
  }
}

// Which file the run directory actually holds: ask for each in turn and keep the
// response of the first that is there, so finding it and reading it are one request.
async function probe(url, tried) {
  for (const f of CANDIDATES) {
    const u = new URL(f, url);
    if (u.href === tried) continue;          // the bootstrap already asked, and missed
    try {
      const r = await fetch(u);
      if (r.ok) return r;
      if (r.body) r.body.cancel();
    } catch (_) { /* keep looking */ }
  }
  return null;
}
